/**
 * Audit-Trail Filter-Leiste v2 (Spec 530, Phase 2, Zone B).
 *
 * Dimension-Tabs (Zeitraum | Akteur | Ressource) statt des fruehereren Dropdowns.
 * Der Zeitraum-Tab fasst die beiden Backend-Zeit-Modi zusammen (Ein Tag = `date`,
 * Von–Bis = `range`). Zusatzfilter (Aktion/Ergebnis) liegen in einem Popover.
 * Die Abfrage laeuft nur bei vollstaendiger UND gueltiger Dimension
 * ({@link isDraftValid}) — `from > to` blockt mit inline-Hinweis.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, SlidersHorizontal } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { AuditQueryMode, AuditQueryParams, AuditResult } from '@/types/audit';

/** Draft-Zustand der Filterleiste (was der Nutzer gerade editiert). */
export interface Draft {
  mode: AuditQueryMode;
  actorId: string;
  resourceType: string;
  resourceId: string;
  date: string;
  from: string;
  to: string;
  action: string;
  result: AuditResult | '';
}

type DimensionTab = 'time' | 'actor' | 'resource';

/** Aktiver Tab aus dem Modus abgeleitet (Zeitraum deckt date + range ab). */
export function tabFromMode(mode: AuditQueryMode): DimensionTab {
  if (mode === 'actor') return 'actor';
  if (mode === 'resource') return 'resource';
  return 'time';
}

/** Draft aus einer committeten Query aufbauen (fuer den Initialzustand). */
export function draftFromQuery(q: AuditQueryParams): Draft {
  return {
    mode: q.mode,
    actorId: q.actorId ?? '',
    resourceType: q.resourceType ?? '',
    resourceId: q.resourceId ?? '',
    date: q.date ?? '',
    from: q.from ?? '',
    to: q.to ?? '',
    action: q.filters?.action ?? '',
    result: q.filters?.result ?? '',
  };
}

/**
 * True, wenn die gewaehlte Dimension vollstaendig UND semantisch gueltig ist.
 * Fuer `range` gilt zusaetzlich `from <= to` (ISO-Datums-Strings sind
 * lexikografisch vergleichbar).
 */
export function isDraftValid(d: Draft): boolean {
  switch (d.mode) {
    case 'actor':
      return d.actorId.trim().length > 0;
    case 'resource':
      return d.resourceType.trim().length > 0 && d.resourceId.trim().length > 0;
    case 'date':
      return d.date.length > 0;
    case 'range':
      return d.from.length > 0 && d.to.length > 0 && d.from <= d.to;
    default:
      return false;
  }
}

/** Baut die Query aus dem Draft (nur die aktive Dimension + Zusatzfilter). */
export function toQuery(d: Draft): AuditQueryParams {
  const filters = {
    ...(d.action ? { action: d.action } : {}),
    ...(d.result ? { result: d.result } : {}),
  };
  return {
    mode: d.mode,
    ...(d.mode === 'actor' ? { actorId: d.actorId.trim() } : {}),
    ...(d.mode === 'resource'
      ? { resourceType: d.resourceType.trim(), resourceId: d.resourceId.trim() }
      : {}),
    ...(d.mode === 'date' ? { date: d.date } : {}),
    ...(d.mode === 'range' ? { from: d.from, to: d.to } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
  };
}

export function AuditFilterBar({
  query,
  onSearch,
  children,
}: {
  /**
   * Die committete Query ist die einzige Quelle der Wahrheit. Der lokale Draft
   * wird daraus geseedet UND bei externen Aenderungen re-synchronisiert (z.B.
   * der Denied-Umschalter aus Phase 3 setzt `filters.result` von aussen).
   */
  query: AuditQueryParams;
  onSearch: (query: AuditQueryParams) => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation('audit');
  const [draft, setDraft] = useState<Draft>(() => draftFromQuery(query));

  // Re-Sync bei externen Query-Aenderungen (committete Query gewinnt). Der erste
  // Lauf (Mount) wird uebersprungen — der Draft ist bereits aus `query` geseedet.
  // Danach feuert die Effect nur bei Query-Identitaetswechsel (Suche/externe
  // Aktion wie der Phase-3-Denied-Umschalter), nicht beim Tippen im Draft.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setDraft(draftFromQuery(query));
  }, [query]);

  const set = (patch: Partial<Draft>) => setDraft((prev) => ({ ...prev, ...patch }));

  const activeTab = tabFromMode(draft.mode);
  const rangeInvalid = draft.mode === 'range' && !!draft.from && !!draft.to && draft.from > draft.to;
  const valid = isDraftValid(draft);
  const extraActive = Boolean(draft.action || draft.result);

  /** Tab-Wechsel: dimensionsspezifische Felder zuruecksetzen, Zusatzfilter erhalten. */
  const onTabChange = (tab: string) => {
    if (tab === 'time') set({ mode: 'date', actorId: '', resourceType: '', resourceId: '' });
    else if (tab === 'actor') set({ mode: 'actor', date: '', from: '', to: '', resourceType: '', resourceId: '' });
    else set({ mode: 'resource', date: '', from: '', to: '', actorId: '' });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (valid) onSearch(toQuery(draft));
  };

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              <TabsTrigger value="time">{t('filter.tab.time')}</TabsTrigger>
              <TabsTrigger value="actor">{t('filter.tab.actor')}</TabsTrigger>
              <TabsTrigger value="resource">{t('filter.tab.resource')}</TabsTrigger>
            </TabsList>

            <TabsContent value="time">
              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={draft.mode === 'date' ? 'default' : 'outline'}
                    onClick={() => set({ mode: 'date', from: '', to: '' })}
                  >
                    {t('filter.timeMode.single')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={draft.mode === 'range' ? 'default' : 'outline'}
                    onClick={() => set({ mode: 'range', date: '' })}
                  >
                    {t('filter.timeMode.range')}
                  </Button>
                </div>
                {draft.mode === 'date' ? (
                  <div className="flex max-w-xs flex-col gap-1.5">
                    <Label htmlFor="audit-date">{t('filter.date')}</Label>
                    <Input id="audit-date" type="date" value={draft.date} onChange={(e) => set({ date: e.target.value })} />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="audit-from">{t('filter.from')}</Label>
                      <Input id="audit-from" type="date" value={draft.from} onChange={(e) => set({ from: e.target.value })} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="audit-to">{t('filter.to')}</Label>
                      <Input id="audit-to" type="date" value={draft.to} onChange={(e) => set({ to: e.target.value })} />
                    </div>
                  </div>
                )}
                {rangeInvalid && (
                  <p className="text-sm text-destructive" role="alert">
                    {t('filter.error.range')}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="actor">
              <div className="flex max-w-md flex-col gap-1.5">
                <Label htmlFor="audit-actor">{t('filter.actorId')}</Label>
                <Input id="audit-actor" value={draft.actorId} onChange={(e) => set({ actorId: e.target.value })} />
              </div>
            </TabsContent>

            <TabsContent value="resource">
              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="audit-rtype">{t('filter.resourceType')}</Label>
                  <Input id="audit-rtype" value={draft.resourceType} onChange={(e) => set({ resourceType: e.target.value })} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="audit-rid">{t('filter.resourceId')}</Label>
                  <Input id="audit-rid" value={draft.resourceId} onChange={(e) => set({ resourceId: e.target.value })} />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                  {t('filter.moreFilters')}
                  {extraActive && (
                    <span
                      className="ml-1 h-2 w-2 rounded-full bg-primary"
                      aria-label={t('filter.moreFiltersActive')}
                    />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="audit-action">{t('filter.action')}</Label>
                  <Input id="audit-action" value={draft.action} onChange={(e) => set({ action: e.target.value })} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="audit-result">{t('filter.result')}</Label>
                  <select
                    id="audit-result"
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={draft.result}
                    onChange={(e) => set({ result: e.target.value as AuditResult | '' })}
                  >
                    <option value="">{t('filter.resultAny')}</option>
                    <option value="success">{t('result.success')}</option>
                    <option value="denied">{t('result.denied')}</option>
                    <option value="error">{t('result.error')}</option>
                  </select>
                </div>
              </PopoverContent>
            </Popover>

            <Button type="submit" className="gap-2" disabled={!valid}>
              <Search className="h-4 w-4" aria-hidden="true" />
              {t('filter.search')}
            </Button>
            {children}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
